import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { provideRouter, RouteReuseStrategy, withComponentInputBinding } from '@angular/router';
import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import { ServiceWorkerModule } from '@angular/service-worker';
import { IonicRouteStrategy, provideIonicAngular } from '@ionic/angular';
import { provideTranslateService, TranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { environment } from '@staffler/env';
import { AuthService, AuthStore } from '@staffler/core';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { httpErrorInterceptor } from './core/interceptors/http-error.interceptor';
import { sessionExpiredInterceptor } from './core/interceptors/session-expired.interceptor';
import { APP_ROUTES } from './app.routes';
import { firstValueFrom } from 'rxjs';
import { Preferences } from '@capacitor/preferences';
import { PREFERENCES_KEYS } from '@staffler/shared';
import { PushNotificationService } from './shared/services/push-notification/push-notification.service';
import { NotificationService } from './modules/notifications/notifications.service';
import { initLanguage } from './core/i18n/i18n.initializer';
import { registerLocaleData } from '@angular/common';
import localeNl from '@angular/common/locales/nl';
import localeFr from '@angular/common/locales/fr';
import * as SentryAngular from '@sentry/angular';

registerLocaleData(localeNl, 'nl');
registerLocaleData(localeFr, 'fr');

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(APP_ROUTES, withComponentInputBinding()),
    provideAppInitializer(async () => {
      const authStore = inject(AuthStore);
      const authService = inject(AuthService);
      const translate = inject(TranslateService);
      const pushNotificationService = inject(PushNotificationService);
      const notificationService = inject(NotificationService);

      await initLanguage(translate);
      await authStore.init();
      if (authStore.isAuthenticated()) {
        try {
          const user = await firstValueFrom(authService.getCurrentUser());
          authStore.setCurrentUser(user);
        } catch {
          return;
        }

        try {
          await notificationService.load();
        } catch (err) {
          console.error('[AppInitializer] Failed to load notifications:', err);
        }

        const { value } = await Preferences.get({ key: PREFERENCES_KEYS.onboardingDone });
        if (value === 'true') {
          await pushNotificationService.init();
        }
      }
    }),
    provideHttpClient(
      withXhr(),
      withInterceptors([httpErrorInterceptor, sessionExpiredInterceptor, authInterceptor])
    ),
    provideIonicAngular({ mode: 'ios' }),
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    provideTranslateService({
      loader: provideTranslateHttpLoader({ prefix: './assets/i18n/', suffix: '.json' }),
    }),
    importProvidersFrom(
      ServiceWorkerModule.register('ngsw-worker.js', {
        enabled: environment.production,
      })
    ),
    {
      provide: ErrorHandler,
      useValue: SentryAngular.createErrorHandler(),
    },
  ],
};
